import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate, useParams } from "react-router-dom";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  NetworkFormErrors,
  NetworkErrors,
  useNetworkById,
  useUpdateNetwork
} from "./networkUtils";
import { useAllSponsors } from "../Sponsors/sponsorUtils";
import NetworkIdentifiersEditor, {
  EDIT_NETWORK_MAX_WIDTH
} from "./NetworkIdentifiersEditor";

const pageDetails: PageWrapperProps = {
  className: "EditNetwork",
  headerText: "Edit Network",
  noScroll: true
};

type NetworkFormInputs = {
  name: string;
  sponsorId: string;
  acgmeEnabled: boolean;
  allAttendingHistoryDisabled: boolean;
  simpl1Id: number | null;
  dashboardUrl: string;
  showDashboardLink: boolean;
  traineeCanRateTrainee: boolean;
  feedbackMinWords: number | null;
};

export const EditNetwork = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    network,
    status: useNetworkStatus,
    error: useNetworkError
  } = useNetworkById(id);
  const { sponsors, status: sponsorsStatus } = useAllSponsors();
  const {
    status: updateNetworkStatus,
    error: updateNetworkError,
    updateNetwork
  } = useUpdateNetwork();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<NetworkFormInputs>({
    defaultValues: {
      name: "",
      sponsorId: "",
      acgmeEnabled: false,
      allAttendingHistoryDisabled: false,
      simpl1Id: null,
      dashboardUrl: "",
      showDashboardLink: false,
      traineeCanRateTrainee: false,
      feedbackMinWords: null
    }
  });

  const [formPopulated, setFormPopulated] = useState(false);

  const statusOfRequiredInitialData = [useNetworkStatus, sponsorsStatus];
  const loadingRequiredInitialData =
    statusOfRequiredInitialData.includes("loading");
  const errorLoadingRequiredInitialData =
    statusOfRequiredInitialData.includes("failed");
  const loadedRequiredInitialData = statusOfRequiredInitialData.every(
    (status) => status === "succeeded"
  );
  const anyApiIsLoading = [
    useNetworkStatus,
    sponsorsStatus,
    updateNetworkStatus
  ].includes("loading");

  const alphabetizedSponsors = sponsors
    ? [...sponsors].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
    : sponsors;

  useEffect(() => {
    if (loadedRequiredInitialData && network) {
      reset({
        name: network.name ?? "",
        sponsorId: network.sponsorId ?? "",
        acgmeEnabled: network.acgmeEnabled ?? false,
        allAttendingHistoryDisabled:
          network.allAttendingHistoryDisabled ?? false,
        simpl1Id: network.simpl1Id ?? null,
        dashboardUrl: network.dashboardURL ?? "",
        showDashboardLink: network.showDashboardLink ?? false,
        traineeCanRateTrainee: network.traineeCanRateTrainee ?? false,
        feedbackMinWords: network.feedbackMinWords ?? null
      });
      setFormPopulated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedRequiredInitialData, network]);

  const onSubmit: SubmitHandler<NetworkFormInputs> = async (data) => {
    if (!id) return;
    const result = await updateNetwork(id, {
      name: data.name,
      sponsorId: data.sponsorId,
      acgmeEnabled: data.acgmeEnabled,
      allAttendingHistoryDisabled: data.allAttendingHistoryDisabled,
      simpl1Id:
        data.simpl1Id === null || Number.isNaN(data.simpl1Id)
          ? null
          : data.simpl1Id,
      dashboardUrl: data.dashboardUrl?.trim() || null,
      showDashboardLink: data.showDashboardLink,
      traineeCanRateTrainee: data.traineeCanRateTrainee,
      feedbackMinWords:
        data.feedbackMinWords && data.feedbackMinWords > 0
          ? data.feedbackMinWords
          : null
    });
    // Only navigate on success — updateNetwork returns null on failure, in which
    // case we stay on the form so the updateNetworkError alert (below) is shown.
    if (result) {
      navigate(`/networks/${id}`);
    }
  };

  if (loadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (errorLoadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error fetching network!")}</h2>
        <p>{useNetworkError}</p>
      </PageWrapper>
    );
  }

  if (loadedRequiredInitialData && !network) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t(NetworkErrors.NetworkNotFound)}</h2>
      </PageWrapper>
    );
  }

  if (loadedRequiredInitialData && network && !formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Populating form...")}</h2>
      </PageWrapper>
    );
  }

  if (updateNetworkStatus === "failed" || updateNetworkError) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(NetworkErrors.UpdateNetworkError)}</h3>
        {updateNetworkError && <p>{updateNetworkError}</p>}
      </PageWrapper>
    );
  }

  if (loadedRequiredInitialData && network && formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <Box
          component="form"
          className="EditNetworkForm"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ maxWidth: EDIT_NETWORK_MAX_WIDTH, mt: 2 }}
        >
          <Grid container spacing={2} rowSpacing={2.5}>
            <Grid xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: NetworkFormErrors.EmptyNetworkName }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    label={t("Network Name")}
                    size="small"
                    required
                    fullWidth
                    disabled={anyApiIsLoading}
                    error={!!errors.name}
                    helperText={
                      errors.name ? t(NetworkFormErrors.EmptyNetworkName) : ""
                    }
                  />
                )}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <Controller
                name="sponsorId"
                control={control}
                rules={{ required: NetworkFormErrors.NoSponsor }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    value={field.value ?? ""}
                    label={t("Sponsor")}
                    size="small"
                    required
                    fullWidth
                    disabled={anyApiIsLoading}
                    error={!!errors.sponsorId}
                    helperText={
                      errors.sponsorId ? t(NetworkFormErrors.NoSponsor) : ""
                    }
                  >
                    <MenuItem value="" disabled>
                      {t("Select a sponsor")}
                    </MenuItem>
                    {alphabetizedSponsors?.map((sponsor) => (
                      <MenuItem key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid xs={12} sm={6} md={4}>
              <Controller
                name="dashboardUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    label={t("Dashboard URL")}
                    placeholder="https://…"
                    size="small"
                    fullWidth
                    disabled={anyApiIsLoading}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} sm={6} md={4}>
              <Controller
                name="feedbackMinWords"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t("Feedback Minimum Word Count")}
                    type="number"
                    size="small"
                    fullWidth
                    disabled={anyApiIsLoading}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(
                        v === "" ? null : Math.max(0, parseInt(v, 10) || 0)
                      );
                    }}
                    inputProps={{ min: 0 }}
                    helperText={t(
                      "Minimum words required in narrative Feedback answers (blank = no minimum)."
                    )}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} sm={6} md={4}>
              <TextField
                label={t("SIMPL 1 ID")}
                size="small"
                fullWidth
                value={network.simpl1Id ?? ""}
                InputProps={{ readOnly: true }}
                disabled
                helperText={t("Managed by SIMPL — not editable")}
              />
            </Grid>

            <Grid xs={12}>
              <FormGroup row sx={{ columnGap: 3, rowGap: 0 }}>
                <Controller
                  name="acgmeEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={anyApiIsLoading}
                        />
                      }
                      label={t("ACGME Enabled")}
                    />
                  )}
                />
                <Controller
                  name="allAttendingHistoryDisabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={anyApiIsLoading}
                        />
                      }
                      label={t("All Attending History Disabled")}
                    />
                  )}
                />
                <Controller
                  name="traineeCanRateTrainee"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={anyApiIsLoading}
                        />
                      }
                      label={t("Trainee Can Rate Trainee")}
                    />
                  )}
                />
                <Controller
                  name="showDashboardLink"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={anyApiIsLoading}
                        />
                      }
                      label={t("Show Dashboard Link")}
                    />
                  )}
                />
              </FormGroup>
            </Grid>

            <Grid xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={anyApiIsLoading}
                >
                  {t("Save")}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={anyApiIsLoading}
                  onClick={() => navigate(`/networks/${id}`)}
                >
                  {t("Cancel")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        {id && <NetworkIdentifiersEditor networkId={id} />}
      </PageWrapper>
    );
  }

  return null;
};
